import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';
import {
  BottomSheetPopup,
  BottomSheetRoot,
  type PhoneLayout,
} from '@/components/ui/phone/BottomSheet';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

/**
 * A panel anchored to an edge of the viewport.
 *
 * Built on the same Base UI dialog as `ui/dialog`, deliberately: the focus
 * trap, the Escape handling, the scroll lock, the portalling and the
 * return-focus-on-close behaviour are the parts that are easy to get subtly
 * wrong and tedious to test, and they are identical for a centred dialog and
 * for an edge-anchored one. What differs is where the popup sits and how it
 * arrives, which is all this file adds.
 *
 * Use a sheet when the content is a *place* — a form with several fields, a
 * detail view, something the person may want to consult the page behind while
 * filling in. Use `ui/dialog` when it is a *question*: a confirm, a short
 * choice, anything whose answer is one click and whose interruption is the
 * point. A destructive confirmation belongs centred, where it blocks.
 *
 * The layout is a full-height flex column, so `SheetHeader` and `SheetFooter`
 * stay put while only the body scrolls. A form long enough to scroll must not
 * push its own submit button out of reach.
 *
 * On a phone a sheet from the right or the bottom is a bottom sheet (spec 261),
 * swiped down to dismiss, as `ui/dialog` is there; one from the left keeps its
 * side, which is where a phone's navigation drawer comes from.
 */
function Sheet({ ...props }: DialogPrimitive.Root.Props) {
  const phone = usePhoneWidth();
  if (phone) {
    return (
      <BottomSheetRoot
        {...(props as React.ComponentProps<typeof BottomSheetRoot>)}
      />
    );
  }
  return <DialogPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        // Dimmed but not blurred, unlike `ui/dialog`. A centred dialog blurs
        // what is behind it because that content is not the point while the
        // dialog is open; a sheet is chosen precisely when it is — you fill in
        // a form here while reading the row, the list or the board it came
        // from. Blurring that away would remove the reason to use a sheet.
        'fixed inset-0 isolate z-50 bg-black/10 duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] data-closed:duration-[var(--motion-duration-base)] data-closed:ease-[var(--motion-ease-exit)] data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

const sheetVariants = cva(
  // A drawer travels the width of itself — roughly 450px — where a dialog fades and
  // zooms five percent on the spot. The scale's `fast` exit is right for the dialog and
  // reads as a snap here, so this surface arrives at `slow` and leaves at `base`: still
  // asymmetric, still only scale values, and the distance is what decides which pair.
  'fixed z-50 flex flex-col bg-popover text-sm text-popover-foreground shadow-floating ring-1 ring-foreground/10 duration-[var(--motion-duration-slow)] ease-[var(--motion-ease-standard)] data-closed:duration-[var(--motion-duration-base)] data-closed:ease-[var(--motion-ease-exit)] outline-none data-open:animate-in data-closed:animate-out',
  {
    variants: {
      side: {
        right:
          'inset-y-0 right-0 h-full w-full border-l border-border sm:max-w-md data-open:slide-in-from-right data-closed:slide-out-to-right',
        left: 'inset-y-0 left-0 h-full w-full border-r border-border sm:max-w-md data-open:slide-in-from-left data-closed:slide-out-to-left',
        bottom:
          'inset-x-0 bottom-0 max-h-[85vh] w-full rounded-t-xl border-t border-border data-open:slide-in-from-bottom data-closed:slide-out-to-bottom',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  phoneLayout = side === 'left' ? 'keep' : 'sheet',
  ...props
}: DialogPrimitive.Popup.Props &
  VariantProps<typeof sheetVariants> & {
    showCloseButton?: boolean;
    /** How it sits on a phone (spec 261): a bottom sheet, unless from the left. */
    phoneLayout?: PhoneLayout;
  }) {
  const phone = usePhoneWidth();
  if (phone && phoneLayout !== 'keep') {
    return (
      <BottomSheetPopup
        {...(props as React.ComponentProps<typeof BottomSheetPopup>)}
        layout={phoneLayout}
        slot="sheet-content"
        closeClassName="top-3 right-3"
        showCloseButton={showCloseButton}
        className={cn('flex flex-col', className)}
      >
        {children}
      </BottomSheetPopup>
    );
  }
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Popup
        data-slot="sheet-content"
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-3 right-3"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </SheetPortal>
  );
}

/** Fixed: it names what is open, so it must not scroll away from the content
 *  it names. `pr-12` keeps the title clear of the close button. */
function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'flex shrink-0 flex-col gap-2 border-b border-border p-5 pr-12',
        className,
      )}
      {...props}
    />
  );
}

/** The only scrolling region. More room at the bottom than at the sides: the
 *  last field or paragraph otherwise ends flush against the sheet's edge, and a
 *  reader takes that as "cut off" rather than "finished". */
function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-body"
      className={cn(
        'scrollbar-slim min-h-0 flex-1 overflow-y-auto p-5 pb-10',
        className,
      )}
      {...props}
    />
  );
}

function SheetFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-muted/30 px-5 py-3.5 sm:flex-row sm:justify-end sm:gap-2.5 [&_[data-slot=button]]:min-w-[5.5rem] [&_[data-slot=button]]:px-4',
        // On a phone's bottom sheet (spec 261): pinned to its bottom edge,
        // opaque, and clear of the home indicator.
        'max-md:sticky max-md:bottom-0 max-md:z-10 max-md:bg-[color-mix(in_oklab,var(--color-muted)_30%,var(--color-popover))] max-md:pb-[max(0.875rem,env(safe-area-inset-bottom))]',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function SheetTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        'font-heading text-base leading-none font-medium',
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  sheetVariants,
};
